import {
  ATTACHMENT_CLOUD_PROVIDERS,
  ATTACHMENT_MAX_BYTES,
  type Attachment,
  type AttachmentCloudProvider,
  type CardDetail,
} from '@molt/shared';
import {
  Download,
  ExternalLink,
  File as FileIcon,
  Link as LinkIcon,
  Pencil,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactElement,
} from 'react';

import {
  Button,
  Dialog,
  EmptyState,
  IconButton,
  TextField,
  profileName,
} from '@/components';
import {
  attachmentDownloadPath,
  formatAttachmentSize,
  isPreviewableAttachment,
  presignAttachmentUpload,
  putAttachmentBytes,
  useCreateCardAttachment,
  useDeleteAttachment,
  useRenameAttachment,
} from '@/lib/attachments';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface CardAttachmentSectionProps {
  readonly card: CardDetail;
  readonly boardId: string;
  readonly canEdit: boolean;
}

type FormMode = 'file' | 'link' | null;

export function CardAttachmentSection({
  card,
  boardId,
  canEdit,
}: CardAttachmentSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const createAttachment = useCreateCardAttachment();
  const renameAttachment = useRenameAttachment();
  const deleteAttachment = useDeleteAttachment();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileDisplayName, setFileDisplayName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDisplayName, setLinkDisplayName] = useState('');
  const [linkCloudProvider, setLinkCloudProvider] = useState<
    AttachmentCloudProvider | ''
  >('');
  const [formError, setFormError] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<Attachment | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [busyMode, setBusyMode] = useState<FormMode>(null);

  const previewIndex = card.attachments.findIndex(
    (attachment) => attachment.id === previewId,
  );
  const previewAttachment =
    previewId === null
      ? null
      : (card.attachments.find((attachment) => attachment.id === previewId) ?? null);
  const uploadBusy =
    busyMode === 'file' || (busyMode === null && createAttachment.isPending);
  const linkBusy =
    busyMode === 'link' ||
    (busyMode === null && formMode === 'link' && createAttachment.isPending);

  const selectedFileTooLarge =
    selectedFile !== null && selectedFile.size > ATTACHMENT_MAX_BYTES;
  const fileName = selectedFile?.name ?? '';
  const fileSubmitDisabled = selectedFile === null || selectedFileTooLarge || uploadBusy;
  const linkSubmitDisabled = linkUrl.trim().length === 0 || linkBusy;

  const readOnlyBody =
    card.attachments.length === 0
      ? t('cardBack.attachments.emptyBodyReadOnly')
      : undefined;

  async function submitFile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selectedFile === null || selectedFileTooLarge) return;
    await uploadFile(selectedFile, fileDisplayName);
  }

  async function uploadFile(file: File, displayName: string) {
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setFormError(
        t('cardBack.attachments.add.tooLarge', {
          size: formatAttachmentSize(t, ATTACHMENT_MAX_BYTES),
        }),
      );
      return;
    }
    setFormError(null);
    setBusyMode('file');
    try {
      const presign = await presignAttachmentUpload(card.id, {
        fileName: file.name,
        sizeBytes: file.size,
        contentType: file.type || 'application/octet-stream',
      });
      await putAttachmentBytes(t, presign, file);
      await createAttachment.mutateAsync({
        cardId: card.id,
        boardId,
        body: {
          kind: 'file',
          storageKey: presign.storageKey,
          displayName: displayName.trim() || null,
        },
      });
      setAnnouncement(
        t('cardBack.attachments.announce.uploadComplete', { file: file.name }),
      );
      setSelectedFile(null);
      setFileDisplayName('');
      if (fileInputRef.current !== null) fileInputRef.current.value = '';
      setFormMode(null);
    } catch (cause) {
      setFormError(
        cause instanceof Error
          ? cause.message
          : t('cardBack.attachments.error.uploadFailed'),
      );
    } finally {
      setBusyMode(null);
    }
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    if (!canEdit) return;
    event.preventDefault();
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    if (!canEdit) return;
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file === undefined) return;
    void uploadFile(file, file.name);
  }

  async function submitLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const url = linkUrl.trim();
    if (url.length === 0) return;
    setFormError(null);
    setBusyMode('link');
    try {
      await createAttachment.mutateAsync({
        cardId: card.id,
        boardId,
        body: {
          kind: 'link',
          url,
          displayName: linkDisplayName.trim() || null,
          cloudProvider: linkCloudProvider || null,
        },
      });
      setAnnouncement(
        t('cardBack.attachments.announce.linkAdded', {
          attachment: linkDisplayName.trim() || url,
        }),
      );
      setLinkUrl('');
      setLinkDisplayName('');
      setLinkCloudProvider('');
      setFormMode(null);
    } catch (cause) {
      setFormError(
        cause instanceof Error
          ? cause.message
          : t('cardBack.attachments.error.linkFailed'),
      );
    } finally {
      setBusyMode(null);
    }
  }

  async function submitRename(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = renameTarget;
    const displayName = renameDraft.trim();
    if (target === null || displayName.length === 0) return;
    await renameAttachment.mutateAsync({
      attachmentId: target.id,
      cardId: card.id,
      boardId,
      body: { displayName },
    });
    setAnnouncement(
      t('cardBack.attachments.announce.renameSaved', { attachment: displayName }),
    );
    setRenameTarget(null);
  }

  function confirmDelete() {
    if (deleteTarget === null) return;
    const target = deleteTarget;
    deleteAttachment.mutate(
      {
        attachmentId: target.id,
        cardId: card.id,
        boardId,
      },
      {
        onSuccess: () => {
          setAnnouncement(
            t('cardBack.attachments.announce.deleteComplete', {
              attachment: target.displayName,
            }),
          );
          setDeleteTarget(null);
          if (previewId === target.id) setPreviewId(null);
        },
      },
    );
  }

  function openRename(attachment: Attachment) {
    setRenameTarget(attachment);
    setRenameDraft(attachment.displayName);
  }

  function movePreview(delta: -1 | 1) {
    if (card.attachments.length === 0) return;
    const current = previewIndex === -1 ? 0 : previewIndex;
    const next = (current + delta + card.attachments.length) % card.attachments.length;
    setPreviewId(card.attachments[next]?.id ?? null);
  }

  return (
    <section
      aria-labelledby="card-attachments-heading"
      className="flex flex-col gap-3"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="card-attachments-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.attachments.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {canEdit ? (
          <>
            <Button
              type="button"
              size="sm"
              variant={formMode === 'file' ? 'toggle' : 'secondary'}
              pressed={formMode === 'file'}
              aria-label={t('cardBack.attachments.add.fileTrigger', {
                card: card.title,
              })}
              iconStart={<Upload aria-hidden="true" className="size-4" />}
              onClick={() => {
                setFormError(null);
                setFormMode(formMode === 'file' ? null : 'file');
              }}
            >
              {t('cardBack.attachments.add.fileTriggerLabel')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={formMode === 'link' ? 'toggle' : 'secondary'}
              pressed={formMode === 'link'}
              aria-label={t('cardBack.attachments.add.linkTrigger', {
                card: card.title,
              })}
              iconStart={<LinkIcon aria-hidden="true" className="size-4" />}
              onClick={() => {
                setFormError(null);
                setFormMode(formMode === 'link' ? null : 'link');
              }}
            >
              {t('cardBack.attachments.add.linkTriggerLabel')}
            </Button>
          </>
        ) : null}
      </div>

      {canEdit && formMode === 'file' ? (
        <form
          className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3"
          onSubmit={(event) => void submitFile(event)}
        >
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('cardBack.attachments.add.file.fileLabel')}
            <input
              ref={fileInputRef}
              type="file"
              className="block w-full rounded-card border border-border-strong bg-surface px-3 py-2 text-sm text-ink file:mr-3 file:rounded-card file:border-0 file:bg-accent file:px-2 file:py-1 file:text-sm file:font-medium file:text-accent-ink"
              onChange={(event) => {
                const next = event.currentTarget.files?.[0] ?? null;
                setSelectedFile(next);
                setFileDisplayName(next?.name ?? '');
                setFormError(null);
              }}
            />
          </label>
          <TextField
            label={t('cardBack.attachments.add.file.displayNameLabel')}
            value={fileDisplayName}
            disabled={selectedFile === null}
            onChange={(event) => setFileDisplayName(event.currentTarget.value)}
          />
          {selectedFileTooLarge ? (
            <p className="text-sm text-bad">
              {t('cardBack.attachments.add.tooLarge', {
                size: formatAttachmentSize(t, ATTACHMENT_MAX_BYTES),
              })}
            </p>
          ) : null}
          {formError !== null ? <p className="text-sm text-bad">{formError}</p> : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-ink-muted">
              {selectedFile === null
                ? t('cardBack.attachments.add.file.noFileSelected')
                : t('cardBack.attachments.add.file.selectedSummary', {
                    file: fileName,
                    size: formatAttachmentSize(t, selectedFile.size),
                  })}
            </p>
            <Button
              type="submit"
              size="sm"
              variant="primary"
              busy={uploadBusy}
              busyLabel={t('cardBack.attachments.add.file.busy')}
              disabled={fileSubmitDisabled}
            >
              {t('cardBack.attachments.add.file.submit')}
            </Button>
          </div>
          {uploadBusy ? (
            <div
              role="progressbar"
              aria-label={t('cardBack.attachments.progress.label')}
              aria-valuetext={t('cardBack.attachments.progress.valueText')}
              className="h-1.5 overflow-hidden rounded-full bg-border"
            >
              <div className="h-full w-1/2 rounded-full bg-accent" />
            </div>
          ) : null}
        </form>
      ) : null}

      {canEdit && formMode === 'link' ? (
        <form
          className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3"
          onSubmit={(event) => void submitLink(event)}
        >
          <TextField
            label={t('cardBack.attachments.add.link.urlLabel')}
            type="url"
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.currentTarget.value)}
          />
          <TextField
            label={t('cardBack.attachments.add.link.displayNameLabel')}
            value={linkDisplayName}
            onChange={(event) => setLinkDisplayName(event.currentTarget.value)}
          />
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('cardBack.attachments.add.link.providerLabel')}
            <select
              className="h-10 rounded-card border border-border-strong bg-surface px-3 text-sm text-ink"
              value={linkCloudProvider}
              onChange={(event) =>
                setLinkCloudProvider(
                  event.currentTarget.value as AttachmentCloudProvider | '',
                )
              }
            >
              <option value="">{t('cardBack.attachments.add.link.noProvider')}</option>
              {ATTACHMENT_CLOUD_PROVIDERS.map((provider) => (
                <option key={provider} value={provider}>
                  {formatCloudProvider(t, provider)}
                </option>
              ))}
            </select>
          </label>
          {formError !== null ? <p className="text-sm text-bad">{formError}</p> : null}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              variant="primary"
              busy={linkBusy}
              busyLabel={t('cardBack.attachments.add.link.busy')}
              disabled={linkSubmitDisabled}
            >
              {t('cardBack.attachments.add.link.submit')}
            </Button>
          </div>
        </form>
      ) : null}

      {uploadBusy && formMode !== 'file' ? (
        <div
          role="progressbar"
          aria-label={t('cardBack.attachments.progress.label')}
          aria-valuetext={t('cardBack.attachments.progress.valueText')}
          className="h-1.5 overflow-hidden rounded-full bg-border"
        >
          <div className="h-full w-1/2 rounded-full bg-accent" />
        </div>
      ) : null}

      {card.attachments.length === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          title={t('cardBack.attachments.emptyTitle')}
          body={canEdit ? t('cardBack.attachments.emptyBody') : readOnlyBody}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {card.attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              attachment={attachment}
              canEdit={canEdit}
              onPreview={() => setPreviewId(attachment.id)}
              onRename={() => openRename(attachment)}
              onDelete={() => setDeleteTarget(attachment)}
            />
          ))}
        </ul>
      )}

      <Dialog
        open={previewAttachment !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewId(null);
        }}
        size="lg"
        title={
          previewAttachment?.displayName ??
          t('cardBack.attachments.preview.titleFallback')
        }
        closeControl={
          <IconButton label={t('cardBack.attachments.preview.close')} size="sm">
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          previewAttachment === null ? undefined : (
            <>
              <Button
                type="button"
                size="sm"
                variant="quiet"
                disabled={card.attachments.length < 2}
                onClick={() => movePreview(-1)}
              >
                {t('cardBack.attachments.preview.previous')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="quiet"
                disabled={card.attachments.length < 2}
                onClick={() => movePreview(1)}
              >
                {t('cardBack.attachments.preview.next')}
              </Button>
              {previewAttachment.kind === 'file' ? (
                <Button
                  as="a"
                  size="sm"
                  variant="secondary"
                  href={attachmentDownloadPath(previewAttachment.id, 'attachment')}
                  target="_blank"
                  rel="noreferrer"
                  iconStart={<Download aria-hidden="true" className="size-4" />}
                >
                  {t('cardBack.attachments.preview.download')}
                </Button>
              ) : (
                <Button
                  as="a"
                  size="sm"
                  variant="secondary"
                  href={previewAttachment.url}
                  target="_blank"
                  rel="noreferrer"
                  iconStart={<ExternalLink aria-hidden="true" className="size-4" />}
                >
                  {t('cardBack.attachments.preview.openLink')}
                </Button>
              )}
            </>
          )
        }
      >
        {previewAttachment === null ? null : (
          <AttachmentPreview attachment={previewAttachment} />
        )}
      </Dialog>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        size="sm"
        title={t('cardBack.attachments.rename.title')}
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => setRenameTarget(null)}>
              {t('cardBack.attachments.cancel')}
            </Button>
            <Button
              type="submit"
              form="card-attachment-rename-form"
              size="sm"
              variant="primary"
              busy={renameAttachment.isPending}
              busyLabel={t('cardBack.attachments.rename.busy')}
              disabled={renameDraft.trim().length === 0}
            >
              {t('cardBack.attachments.rename.submit')}
            </Button>
          </>
        }
      >
        <form
          id="card-attachment-rename-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => void submitRename(event)}
        >
          <TextField
            label={t('cardBack.attachments.rename.nameLabel')}
            value={renameDraft}
            onChange={(event) => setRenameDraft(event.currentTarget.value)}
          />
        </form>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        size="sm"
        title={t('cardBack.attachments.delete.title')}
        description={
          deleteTarget === null
            ? undefined
            : t('cardBack.attachments.delete.description', {
                attachment: deleteTarget.displayName,
              })
        }
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => setDeleteTarget(null)}>
              {t('cardBack.attachments.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="danger"
              busy={deleteAttachment.isPending}
              busyLabel={t('cardBack.attachments.delete.busy')}
              onClick={confirmDelete}
            >
              {t('cardBack.attachments.delete.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('cardBack.attachments.delete.undoNotice')}
        </p>
      </Dialog>

      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </section>
  );
}

function AttachmentRow({
  attachment,
  canEdit,
  onPreview,
  onRename,
  onDelete,
}: {
  readonly attachment: Attachment;
  readonly canEdit: boolean;
  readonly onPreview: () => void;
  readonly onRename: () => void;
  readonly onDelete: () => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const isFile = attachment.kind === 'file';
  const size = isFile
    ? formatAttachmentSize(t, attachment.sizeBytes)
    : t('cardBack.attachments.item.linkKind');
  // `uploadedBy` is null once the uploader's account is deleted
  // (`fk_attachments_uploaded_by_user_id` is `ON DELETE SET NULL`). The ternary
  // is not redundant: `profileName(null)` returns the English constant, not
  // the catalogue's value — the same branch `CommentItem` keeps.
  const uploaderName =
    attachment.uploadedBy === null
      ? t('primitives.avatar.deletedAccount')
      : profileName(attachment.uploadedBy);

  return (
    <li>
      <article
        aria-label={t('cardBack.attachments.item.label', {
          attachment: attachment.displayName,
        })}
        className="flex items-start gap-3 rounded-card border border-border bg-surface-raised p-3"
      >
        <div
          aria-hidden="true"
          className={cn(
            'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-card border border-border-strong',
            isFile ? 'bg-surface text-ink-muted' : 'bg-accent text-accent-ink',
          )}
        >
          {isImageAttachment(attachment) ? (
            <img
              src={attachmentDownloadPath(attachment.id)}
              alt=""
              title={t('cardBack.attachments.item.thumbnail', {
                attachment: attachment.displayName,
              })}
              className="size-full rounded-[calc(var(--radius-card)-1px)] object-cover"
            />
          ) : isFile ? (
            <FileIcon className="size-4" />
          ) : (
            <LinkIcon className="size-4" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <button
              type="button"
              className="min-w-0 text-left text-sm font-medium text-ink underline-offset-2 hover:underline"
              onClick={onPreview}
            >
              <span className="break-words">{attachment.displayName}</span>
            </button>
            {attachment.kind === 'link' && attachment.cloudProvider !== null ? (
              <span className="text-xs text-ink-muted">
                {formatCloudProvider(t, attachment.cloudProvider)}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-ink-muted">
            {t('cardBack.attachments.item.meta', {
              size,
              name: uploaderName,
              date: formatAttachmentTimestamp(attachment.createdAt),
            })}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isFile ? (
            <IconButton
              label={t('cardBack.attachments.item.download', {
                attachment: attachment.displayName,
              })}
              size="sm"
              onClick={() => {
                window.open(
                  attachmentDownloadPath(attachment.id, 'attachment'),
                  '_blank',
                  'noreferrer',
                );
              }}
            >
              <Download aria-hidden="true" className="size-4" />
            </IconButton>
          ) : (
            <IconButton
              label={t('cardBack.attachments.item.open', {
                attachment: attachment.displayName,
              })}
              size="sm"
              onClick={() => {
                window.open(attachment.url, '_blank', 'noreferrer');
              }}
            >
              <ExternalLink aria-hidden="true" className="size-4" />
            </IconButton>
          )}
          {canEdit ? (
            <>
              <IconButton
                label={t('cardBack.attachments.item.rename', {
                  attachment: attachment.displayName,
                })}
                size="sm"
                onClick={onRename}
              >
                <Pencil aria-hidden="true" className="size-4" />
              </IconButton>
              <IconButton
                label={t('cardBack.attachments.item.delete', {
                  attachment: attachment.displayName,
                })}
                size="sm"
                onClick={onDelete}
              >
                <Trash2 aria-hidden="true" className="size-4" />
              </IconButton>
            </>
          ) : null}
        </div>
      </article>
    </li>
  );
}

function AttachmentPreview({
  attachment,
}: {
  readonly attachment: Attachment;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  if (attachment.kind === 'link') {
    return (
      <div className="flex flex-col gap-3">
        <p className="break-all text-sm text-ink-muted">{attachment.url}</p>
        <Button
          as="a"
          size="sm"
          variant="secondary"
          href={attachment.url}
          target="_blank"
          rel="noreferrer"
          iconStart={<ExternalLink aria-hidden="true" className="size-4" />}
          className="self-start"
        >
          {t('cardBack.attachments.preview.openLink')}
        </Button>
      </div>
    );
  }

  const downloadPath = attachmentDownloadPath(attachment.id);
  if (!isPreviewableAttachment(attachment)) {
    return (
      <div className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-4">
        <p className="text-sm text-ink-muted">
          {t('cardBack.attachments.preview.notPreviewable')}
        </p>
        <Button
          as="a"
          size="sm"
          variant="secondary"
          href={attachmentDownloadPath(attachment.id, 'attachment')}
          target="_blank"
          rel="noreferrer"
          iconStart={<Download aria-hidden="true" className="size-4" />}
          className="self-start"
        >
          {t('cardBack.attachments.preview.download')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <iframe
        title={t('cardBack.attachments.preview.frameTitle', {
          attachment: attachment.displayName,
        })}
        src={downloadPath}
        className="h-[28rem] w-full rounded-card border border-border-strong bg-surface-raised"
      />
      <p className="text-sm text-ink-muted">
        {t('cardBack.attachments.preview.loadHint')}
      </p>
    </div>
  );
}

function formatAttachmentTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCloudProvider(
  t: InterfaceLanguageContextValue['t'],
  value: AttachmentCloudProvider,
): string {
  switch (value) {
    case 'google_drive':
      return t('cardBack.attachments.cloudProvider.googleDrive');
    case 'dropbox':
      return t('cardBack.attachments.cloudProvider.dropbox');
    case 'box':
      return t('cardBack.attachments.cloudProvider.box');
    case 'onedrive':
      return t('cardBack.attachments.cloudProvider.oneDrive');
  }
}

function isImageAttachment(
  attachment: Attachment,
): attachment is Extract<Attachment, { kind: 'file' }> {
  if (attachment.kind !== 'file') return false;
  const extension = attachment.displayName.split('.').at(-1)?.toLowerCase();
  return (
    extension !== undefined &&
    ['apng', 'avif', 'bmp', 'gif', 'jpeg', 'jpg', 'png', 'svg', 'webp'].includes(
      extension,
    )
  );
}
